import { useEffect, useState } from 'react';
import { Icon } from '../../Atoms/Icon';
import { SrOnlyText, TextSpan } from '../../Atoms/Text';
import { IconButton, TextField } from '@mui/material';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import NewLocationInput from './NewLocationInput';
import { NewLocationEntry } from '../../../Api/ServiceRequest/serviceRequestApi';

const helperStyle = {
  color: 'rgba(0, 0, 0, 0.6)',
  fontFamily: 'sans-serif',
  fontWeight: '400',
  fontSize: '14px',
  lineHeight: '1.66',
  letterSpacing: '0.03333em',
  marginTop: '4px',
  marginRight: '14px',
  marginBottom: '0',
  marginLeft: '14px'
};

export interface MultiNewLocationInputProps {
  linkClassName?: string;
  limit?: number;
  limitText?: string;
  onLocationsChange?: (locations: NewLocationEntry[]) => void;
  showAdvancedSettings?: boolean;
  initialLocations?: NewLocationEntry[];
}

export const MultiNewLocationInput = ({
  linkClassName,
  limit = 0,
  limitText = '',
  onLocationsChange,
  showAdvancedSettings = false,
  initialLocations = []
}: MultiNewLocationInputProps) => {
  const [locations, setLocations] = useState<NewLocationEntry[]>(initialLocations);
  const [locationKeys, setLocationKeys] = useState<number[]>(initialLocations.map((_, i) => i));

  useEffect(() => {
    onLocationsChange && onLocationsChange(locations);
  }, [locations]);

  const addLocation = () => {
    setLocations([...locations, { address: '' }]);
    const newKey = locationKeys.length == 0 ? 0 : locationKeys[locationKeys.length - 1] + 1;
    setLocationKeys([...locationKeys, newKey]);
  };

  const updateLocationField = (index: number, field: keyof NewLocationEntry, value: string) => {
    const newLoc = [...locations];
    newLoc[index] = { ...newLoc[index], [field]: value };
    setLocations(newLoc);
  };

  const removeLocation = (indexToRemove: number) => {
    setLocations(locations.filter((_, index) => index !== indexToRemove));
    setLocationKeys(locationKeys.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="w-full flex flex-col gap-2">
      {locations.map((location, index) => (
        <div
          key={locationKeys[index]}
          className="w-full flex flex-row gap-2">
          <div className="flex flex-col gap-2 flex-1">
            <NewLocationInput
              size="small"
              value={location.address}
              onChange={(newValue) => updateLocationField(index, 'address', newValue)}
              required={true}
              label={`New Location ${index + 1} Address`}
            />
            {showAdvancedSettings && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  label={`Add Location ${index + 1} Code`}
                  value={location.locationCode ?? ''}
                  onChange={(e) => updateLocationField(index, 'locationCode', e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={`Add Location ${index + 1} Name`}
                  value={location.locationName ?? ''}
                  onChange={(e) => updateLocationField(index, 'locationName', e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={`Add Location ${index + 1} Floor, Suite & Room`}
                  value={location.floorSuiteRoom ?? ''}
                  onChange={(e) => updateLocationField(index, 'floorSuiteRoom', e.target.value)}
                />
              </>
            )}
          </div>
          <IconButton
            sx={{
              margin: 'auto'
            }}
            color="warning"
            onClick={(e) => {
              e.preventDefault();
              removeLocation(index);
            }}>
            <RemoveCircleIcon />
            <SrOnlyText>Remove Location {index + 1}</SrOnlyText>
          </IconButton>
        </div>
      ))}
      <>
        {limit > 0 && locations.length >= limit && <p style={helperStyle}>{limitText}</p>}
        {!(limit > 0 && locations.length >= limit) && (
          <TextSpan className="flex items-center space-x-2">
            <button
              className={`${linkClassName} text-grey-12 flex flex-row gap-2 items-center`}
              data-cy="new-location"
              disabled={limit > 0 && locations.length >= limit}
              onClick={(e) => {
                e.preventDefault();
                addLocation();
              }}>
              <Icon type="plus" /> New Location
            </button>
          </TextSpan>
        )}
      </>
    </div>
  );
};

export default MultiNewLocationInput;
